import { useDispatch } from 'react-redux'
import { Spacer, Text, Container, Form, Button } from 'roadie-ui'
import { PanelSubheading } from 'components/layout/Panel'

import { states } from 'types/states'
import { addBusinessInfo, deleteBusinessInfo } from 'actions/profile'
import React, { useState } from 'react'
import SectionMessage from 'components/layout/SectionMessage'
import { EINVerification } from 'types/profile'
import { LocalizedRichText, useLocalization, LocalizedText } from 'localization'

// For businessName, allow alphanumeric characters as well as - &
export const allowedRegexBusinessName = /^[a-zA-Z0-9\-& ]+$/

export const handleBusinessNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (!allowedRegexBusinessName.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') {
    e.preventDefault()
  }
}

export const handleBusinessNamePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
  const pastedText = e.clipboardData.getData('text')
  if (!allowedRegexBusinessName.test(pastedText)) {
    e.preventDefault()
  }
}

export const BusinessInfoForm: React.FC<{
  einVerification?: EINVerification
  page?: string
}> = (props) => {
  const { t } = useLocalization()
  const dispatch = useDispatch()
  const einData = props.einVerification?.ein_verification

  const [updating, setUpdating] = useState(einData ? false : true)

  const [businessType, setBusinessType] = useState(
    einData?.multi_member_ein != null
      ? einData?.multi_member_ein
        ? 'multi-member'
        : 'single-member'
      : 'sole-proprietor'
  )
  const [businessName, setBusinessName] = useState(einData?.business_name || '')
  const [state, setState] = useState(einData?.state_of_incorporation || '')
  const [ein, setEin] = useState(einData?.ein || '')
  const status = einData?.status || ''

  const handleSubmit = async () => {
    try {
      if (businessType == 'sole-proprietor') {
        await deleteBusinessInfo(einData?.id)(dispatch)
        dispatch({ type: 'PUSH_FLASH', flash: { messageKey: 'dweb_flash_business_info_saved', type: 'success' } })
      } else {
        if (businessName == '' || state == '') {
          dispatch({
            type: 'PUSH_FLASH',
            flash: { type: 'error', messageKey: 'dweb_flash_business_name_and_state_required' },
          })
          return
        }
        if (!allowedRegexBusinessName.test(businessName)) {
          dispatch({
            type: 'PUSH_FLASH',
            flash: {
              type: 'error',
              messageKey: 'dweb_flash_business_name_allowed_characters',
            },
          })
          return
        }
        const multiMemberEIN = businessType == 'multi-member' ? true : false
        const EIN = multiMemberEIN ? '' : ein
        await addBusinessInfo(businessName, state, EIN, multiMemberEIN)(dispatch)
        dispatch({ type: 'PUSH_FLASH', flash: { messageKey: 'dweb_flash_business_info_saved', type: 'success' } })
      }
    } catch (err: any) {
      // TODO: create type for this error
      const errors = err.response.data.errors
      const errorType = Object.keys(errors)[0]
      switch (errorType) {
        case 'business_name':
          dispatch({ type: 'PUSH_FLASH', flash: { type: 'error', messageKey: 'dweb_flash_business_name_invalid' } })
          break
        case 'ein':
          if (errors['ein'][0] == 'already exists') {
            dispatch({
              type: 'PUSH_FLASH',
              flash: {
                type: 'error',
                messageKey: 'dweb_flash_ein_already_exists_single_member_only',
              },
            })
          } else {
            dispatch({ type: 'PUSH_FLASH', flash: { type: 'error', messageKey: 'dweb_flash_ein_invalid' } })
          }
          break
        default:
          dispatch({ type: 'PUSH_FLASH', flash: { type: 'error', messageKey: 'dweb_flash_generic_error_occurred' } })
      }
    }
  }

  return (
    <Form.Basic
      onSubmit={handleSubmit}
      validations={[
        ['ein', Form.validations.exactLength(9), t('dweb_validation_invalid_ein_must_be_9_digits')],
        ['ein', Form.validations.ein],
      ]}
    >
      {props.page == 'tax-info' && (
        <>
          <Spacer $height="2.0rem" />
          <Text.Body1 $color="neutral-black" $textAlign="start">
            <LocalizedText translationKey="dweb_account_basic_info_business_info_current_year_future_tax_years_all_1099s_will" />
          </Text.Body1>
          <Spacer $height="2.0rem" />
        </>
      )}

      {einData?.multi_member_ein != null &&
        !updating &&
        (einData?.multi_member_ein == true ? (
          <>
            <Text.Body1 $color="neutral-black" $fontSize="medium">
              <LocalizedRichText
                translationKey="dweb_account_basic_info_business_info_indicated_strong_text1_following_information"
                params={{
                  strongText1: (
                    <strong>
                      <LocalizedText translationKey="dweb_account_basic_info_business_info_operate_under_multi_member_llc_corporation" />
                    </strong>
                  ),
                }}
              />
            </Text.Body1>
          </>
        ) : (
          <>
            <Text.Body1 $color="neutral-black" $fontSize="medium">
              <LocalizedRichText
                translationKey="dweb_account_basic_info_business_info_indicated_strong_text1_following_information"
                params={{
                  strongText1: (
                    <strong>
                      <LocalizedText translationKey="dweb_account_basic_info_business_info_operate_single_member_llc_sole_proprietorship" />
                    </strong>
                  ),
                }}
              />
            </Text.Body1>
            <Spacer $height="2.0rem" />
          </>
        ))}

      {props.page != 'tax-info' && updating && (
        <Form.Radio.Group
          defaultValue="sole-proprietor"
          value={businessType}
          onChange={(_, value) => setBusinessType(value)}
        >
          <Text.Body1 $color="neutral-black" $fontWeight="bold" $fontSize="medium">
            <LocalizedText translationKey="dweb_account_basic_info_business_info_select_which_following_applies_situation" />
          </Text.Body1>
          <Spacer $height="2.0rem" />
          <Form.Radio.Field
            label={t('dweb_account_basic_info_business_info_i_am_sole_proprietor')}
            value="sole-proprietor"
          >
            <Text.Body1 $color="neutral-black" $fontSize="medium">
              <LocalizedText translationKey="dweb_account_basic_info_business_info_i_am_sole_proprietor" />
            </Text.Body1>
          </Form.Radio.Field>
          <Spacer $height="2.0rem" />
          <Form.Radio.Field label={t('dweb_business_info_single_member_option')} value="single-member">
            <Text.Body1 $color="neutral-black" $fontSize="medium">
              <LocalizedText translationKey="dweb_business_info_single_member_option" />
            </Text.Body1>
          </Form.Radio.Field>
          <Spacer $height="2.0rem" />
          <Form.Radio.Field
            label={t('dweb_account_basic_info_business_info_i_operate_under_multi_member_llc_corporation')}
            value="multi-member"
          >
            <Text.Body1 $color="neutral-black" $fontSize="medium">
              <LocalizedText translationKey="dweb_account_basic_info_business_info_i_operate_under_multi_member_llc_corporation" />
            </Text.Body1>
          </Form.Radio.Field>
        </Form.Radio.Group>
      )}

      {businessType == 'multi-member' && (
        <>
          <Spacer $height="2.0rem" />
          <SectionMessage description={t('dweb_business_info_multi_member_not_supported')} iconType="warning" />
          <Spacer $height="2.0rem" />
        </>
      )}

      {businessType != 'sole-proprietor' && (
        <>
          <Form.Input
            label={t('dweb_account_basic_info_registration_business_name')}
            name="business_name"
            disabled={!!einData?.business_name && !updating}
            type="text"
            defaultValue={businessName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBusinessName(e.target.value)}
            onKeyDown={handleBusinessNameKeyDown}
            onPaste={handleBusinessNamePaste}
            maxLength={40}
          />
          <Spacer $height="2.0rem" />
          <Form.Select
            label={t('dweb_account_basic_info_registration_state_registration')}
            name="business_state"
            disabled={!!einData?.state_of_incorporation && !updating}
            defaultValue={state}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setState(e.target.value)}
          >
            {states.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </Form.Select>
          <Spacer $height="2.0rem" />

          {props.page != 'tax-info' &&
            businessType != 'sole-proprietor' &&
            ((updating && businessType == 'single-member') || ein ? (
              <>
                <Form.Input
                  label={t('dweb_account_basic_info_business_info_ein_optional')}
                  name="ein"
                  disabled={!updating || businessType == 'multi-member'}
                  type="ein"
                  defaultValue={ein}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEin(e.target.value.replace('-', ''))}
                  maxLength={10} // includes the dash
                />
                <Spacer $height="2.0rem" />
              </>
            ) : (
              businessType == 'single-member' && (
                <>
                  <Text.Body1 $color="neutral-darker" $fontSize="medium" $textAlign="center">
                    <LocalizedText translationKey="dweb_account_basic_info_business_info_no_ein_was_provided" />
                  </Text.Body1>
                  <Spacer $height="2.0rem" />
                </>
              )
            ))}

          {!einData?.ein ? (
            props.page == 'tax-info' && businessType != 'multi-member' ? (
              <Text.Body1 $textAlign="start">
                <LocalizedText translationKey="dweb_account_basic_info_business_info_no_ein_file_1099_will_filed_ssn" />
              </Text.Body1>
            ) : (
              !einData &&
              businessType == 'single-member' && (
                <PanelSubheading>
                  <LocalizedText translationKey="dweb_business_info_ein_verification_notice" />
                </PanelSubheading>
              )
            )
          ) : (
            !updating && (
              <>
                {status == 'pending' && (
                  <SectionMessage
                    description={t('dweb_account_basic_info_business_info_ein_has_not_yet_been_verified_well_contact')}
                    iconType="warning"
                  />
                )}
                {status == 'verified' && (
                  <SectionMessage
                    description={t('dweb_account_basic_info_business_info_ein_has_been_verified_irs')}
                    iconType="success"
                  />
                )}
              </>
            )
          )}
        </>
      )}

      {props.page != 'tax-info' &&
        (!einData?.business_name ? (
          businessType != 'sole-proprietor' && (
            <>
              <Spacer $height="2.0rem" />
              <Form.Submit>
                <LocalizedText translationKey="dweb_account_basic_info_business_info_save_business_info" />
              </Form.Submit>
            </>
          )
        ) : updating ? (
          <>
            <Spacer $height="2.0rem" />
            <Container.Grid $gridTemplateColumns="1fr 1fr" $gridGap="3px">
              <Button.Tertiary onClick={() => setUpdating(false)} $width="100%">
                <LocalizedText translationKey="dweb_common_cancel" />
              </Button.Tertiary>
              <Form.Submit>
                <LocalizedText translationKey="dweb_common_update" />
              </Form.Submit>
            </Container.Grid>
          </>
        ) : (
          <>
            <Spacer $height="2.0rem" />
            <Button.Secondary onClick={() => setUpdating(true)} $width="100%">
              <LocalizedText translationKey="dweb_account_basic_info_business_info_update_information" />
            </Button.Secondary>
          </>
        ))}

      <Spacer $height="2.0rem" />
    </Form.Basic>
  )
}
